import React, { useEffect, useState } from 'react'
import commerce from '@lib/api/commerce'
import { FAQ, Layout, SEO } from '@components/common'

import { useUI } from '@components/ui/context'

import { getGlobalContentContext } from '@drf/getGlobalContentContext'
import { getPageContext } from '@drf/getPageContext'
import type {
  GetStaticPropsContext,
  InferGetServerSidePropsType,
  InferGetStaticPropsType,
} from 'next'
import { useGlobalContentContext } from '@context/GlobalContentContext'
import { gsap } from 'gsap/dist/gsap'
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin'
import {
  Hero,
  ProductDetails,
  ScanningSection,
  Performance,
  ComparisonSection,
} from '@components/lorethreepage'
import { Container } from '@components/ui'
import { LoreThreePage } from '@generated/model/loreThreePage'

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(ScrollToPlugin)
}

export async function getStaticProps({
  preview,
  locale,
  locales,
}: GetStaticPropsContext) {
  const config = { locale, locales }
  const productsPromise = commerce.getAllProducts({
    variables: { first: 50 },
    config,
    preview,
    // Saleor provider only
    ...({ featured: true } as any),
  })
  const pagesPromise = commerce.getAllPages({ config, preview })
  const siteInfoPromise = commerce.getSiteInfo({ config, preview })
  const { products } = await productsPromise

  const { product: roadProduct } = await commerce.getProduct({
    variables: { slug: 'lorethree-road' },
    config,
    preview,
  })

  const { product: offRoadProduct } = await commerce.getProduct({
    variables: { slug: 'lorethree-off-road' },
    config,
    preview,
  })

  const { pages } = await pagesPromise
  const { categories, brands } = await siteInfoPromise

  const global = await getGlobalContentContext()
  const page: LoreThreePage = await getPageContext('lorethreepage')

  const performance = page.performance_sections

  // const fittersBlock = await getPageContext('findfittersblock')
  // const fittersMap = await getPageContext('fitters')
  // const fitters = Object.keys(fittersMap).map((i) => fittersMap[i])

  return {
    props: {
      products,
      roadProduct,
      offRoadProduct,
      categories,
      brands,
      pages,
      global,
      page,
      // fittersBlock,
      // fitters,
      performance,
    },
    revalidate: 60,
  }
}

export default function Home({
  roadProduct,
  offRoadProduct,
  global,
  page,
  // fittersBlock,
  // fitters,
  performance,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const { setData } = useGlobalContentContext()

  useEffect(() => {
    setData(global)
  }, [global, setData])

  const { setPageTheme, pageTheme, setContainerFixedWidth } = useUI()

  useEffect(() => {
    setContainerFixedWidth()
    pageTheme != 'LIGHT' && setPageTheme('LIGHT')
  }, [])

  // const { isLoaded } = useJsApiLoader({
  //   id: 'google-map-script',
  //   googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string,
  //   libraries: ['places'],
  // })

  return (
    <>
      <div>
        {page.hero && <Hero page={page} />}

        <ProductDetails
          page={page}
          roadProduct={roadProduct}
          offRoadProduct={offRoadProduct}
        />

        <ScanningSection page={page} />

        {performance && (
          <Performance data={{ sections: performance, navigation_title: '' }} />
        )}

        <ComparisonSection page={page} />

        {page.faqblock && (
          <Container>
            <FAQ
              title={page.faqblock.title}
              faqs={page.faqblock.faqs}
              expandAll={false}
            />
          </Container>
        )}
      </div>

      <SEO
        title={page.meta_title}
        description={page.meta_description}
        openGraph={{
          type: 'website',
          title: page.meta_title,
          description: page.meta_description,
          images: [
            {
              url: page.meta_image as string,
              width: '800',
              height: '600',
              alt: page.meta_title,
            },
          ],
        }}
      />
    </>
  )
}

Home.Layout = Layout
